<template>
    <div class="sidebar" :class="{ floating }" id="global-sidebar">
        <div class="main-menu" :class="{ show: mobileVisible }">
            <component
                :is="simpleSidebar ? 'div' : 'router-link'"
                class="sidebar-top-item logo"
                :class="{ 'cursor-default': simpleSidebar, 'no-hover': simpleSidebar }"
                :to="simpleSidebar ? undefined : { name: 'home' }"
                @click="$inLTI && closeSubMenu(true)"
            >
                <cg-logo :small="!mobileVisible" :inverted="darkMode || $inLTI" />
            </component>
            <hr class="separator" />
            <div class="sidebar-top">
                <transition
                    v-for="entry in entries"
                    :key="`sidebar-transition-${entry.name}`"
                    v-if="maybeCall(entry.condition)"
                    name="pop-in"
                    :appear="loaded"
                    :enter-active-class="
                        entry.animate || entry.animateAdd ? 'pop-in-enter-active' : ''
                    "
                    :leave-active-class="
                        entry.animate || entry.animateRemove ? 'pop-in-leave-active' : ''
                    "
                >
                    <a
                        @click="openUpperSubMenu(entry, true)"
                        @keyup.enter="openUpperSubMenu(entry, true)"
                        tabindex="0"
                        class="sidebar-top-item sidebar-entry position-relative"
                        :class="{
                            selected: currentEntry && entry.name === currentEntry.name,
                            [`sidebar-entry-${entry.name}`]: true,
                        }"
                    >
                        <icon
                            :icon="entry.icon"
                            :style="{ color: entry.iconColor != null ? entry.iconColor() : '' }"
                            :scale="mobileVisible ? 1 : 2"
                            :label="maybeCall(entry.title || entry.header)"
                        />
                        <notification-bell v-if="entry.showUserNotifications" />
                        <small class="name">
                            <vnode :fun="entry.title || entry.header" />
                        </small>
                    </a>
                </transition>
            </div>

            <hr class="separator" />

            <div v-if="canManageCurrentLtiAssignment">
                <div class="sidebar-bottom">
                    <submit-button
                        :submit="openRouteInTab"
                        class="new-tab-link sidebar-bottom-item d-inline border-0 rounded-0 shadow-none"
                        variant="secondary"
                        @success="afterOpenRouteInTab"
                        v-b-popover.top.hover.focus="'Open this page in a new tab'"
                    >
                        <div class="new-tab-wrapper">
                            <icon icon="fa-solid fa-share-from-square" :scale="1" class="mr-2" />
                            <small class="name new-tab">New tab</small>
                        </div>
                    </submit-button>
                </div>

                <hr class="separator" />
            </div>

            <announcements />

            <div v-if="adminRoute.isJust()">
                <div class="sidebar-bottom">
                    <router-link
                        :to="adminRoute.extract()"
                        class="sidebar-bottom-item sidebar-entry-admin"
                        v-b-popover.hover.focus.top="'Admin'"
                    >
                        <icon icon="fa-solid fa-screwdriver-wrench" />
                    </router-link>
                </div>

                <hr class="separator" />
            </div>

            <toggle-student-view-button :logged-in-user="loggedInUser" v-if="loggedIn" />

            <div class="sidebar-bottom">
                <help-homepage-link
                    class="sidebar-bottom-item"
                    v-b-popover.hover.focus.top="'Documentation'"
                >
                    <icon icon="fa-solid fa-question" />
                </help-homepage-link>

                <cg-submit-button
                    :submit="logout"
                    class="sidebar-bottom-item d-inline border-0 rounded-0 shadow-none logout-btn"
                    v-b-popover.hover.focus.top="'Log out'"
                    v-if="loggedIn && !$inLTI"
                    aria-label="logout"
                >
                    <icon icon="fa-solid fa-power-off" />
                </cg-submit-button>
            </div>
        </div>

        <div class="shadow-overlay" />

        <div
            class="submenu-container"
            :class="{ 'use-space': dimmingUseSpace }"
            ref="subMenuContainer"
            v-if="subMenus.length"
        >
            <div class="submenus">
                <div
                    v-for="(subMenu, i) in subMenus"
                    class="submenu theme-main-background"
                    :id="`submenu-${i}`"
                    :style="subMenuStyle(subMenu)"
                >
                    <header>
                        <div
                            class="action back-button"
                            v-b-popover.hover.bottom.focus="
                                i ? 'Back to ' + maybeCall(subMenus[i - 1].header) : 'Close submenu'
                            "
                            @click="closeSubMenu()"
                            @keyup.enter="closeSubMenu()"
                            role="button"
                            :aria-label="
                                i ? 'Back to ' + maybeCall(subMenus[i - 1].header) : 'Close submenu'
                            "
                            tabindex="0"
                        >
                            <icon :icon="i ? 'fa-solid fa-arrow-left' : 'fa-solid fa-xmark'" />
                        </div>

                        <h4 class="submenu-header">
                            <vnode :fun="subMenu.header" />
                        </h4>

                        <div
                            v-if="subMenu.reload || loading"
                            @click="refreshItems"
                            @keyup.enter="refreshItems"
                            class="action refresh-button"
                            v-b-popover.hover.focus.bottom="'Refresh'"
                            tabindex="0"
                            role="button"
                            aria-label="Refresh"
                        >
                            <icon icon="fa-solid fa-rotate" :spin="loading" />
                        </div>
                    </header>

                    <hr class="separator" />

                    <component
                        :is="subMenu.component"
                        v-show="subMenu.showWhenLoading || !loading"
                        :data="maybeCall(subMenu.data)"
                        @loading="loading = true"
                        @loaded="loading = false"
                        @open-menu="openSubMenu"
                        @close-menu="closeSubMenu(true)"
                    />
                    <cg-loader v-if="loading && !subMenu.showWhenLoading" :scale="1" class="pt-2" />
                </div>
            </div>
        </div>

        <div class="page-overlay" v-if="dimPage" @click="closeSubMenu(true)" />
    </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faPowerOff } from '@fortawesome/free-solid-svg-icons/faPowerOff';
import { faQuestion } from '@fortawesome/free-solid-svg-icons/faQuestion';
import { faScrewdriverWrench } from '@fortawesome/free-solid-svg-icons/faScrewdriverWrench';
import { faShareFromSquare } from '@fortawesome/free-solid-svg-icons/faShareFromSquare';
import { faBook } from '@fortawesome/pro-regular-svg-icons/faBook';
import { faCircleUser } from '@fortawesome/pro-regular-svg-icons/faCircleUser';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons/faPenToSquare';
import { faRightToBracket } from '@fortawesome/pro-regular-svg-icons/faRightToBracket';
import { faRocket } from '@fortawesome/pro-regular-svg-icons/faRocket';
import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons/faMagnifyingGlass';
import { mapActions, mapGetters } from 'vuex';

import { Maybe } from '@codegrade/maybe';

import { CourseName, HelpHomepageLink, Loader, SubmitButton } from '@/components';
import Icon from '@/components/Icon';
import { loggedInUserSessionParser } from '@/models/user';
import { SiteSettingsStore, UIPrefsStore, UserStore } from '@/store';
import * as utils from '@/utils';

import { Announcements } from '../Announcements';
import CgLogo from '../CgLogo';
import NotificationBell from '../NotificationBell';
import AssignmentList from './AssignmentList';
import CourseList from './CourseList';
import GroupList from './GroupList';
import PlagiarismCaseList from './PlagiarismCaseList';
import SubmissionsSidebarList from './SubmissionsSidebarList';
import ToggleStudentViewButton from './ToggleStudentViewButton';
import UserInfo from './UserInfo';

library.add(faPowerOff, faScrewdriverWrench, faQuestion, faShareFromSquare);

export default {
    name: 'sidebar',

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    data() {
        return {
            loading: true,
            loaded: false,
            entries: [
                {
                    name: 'login',
                    icon: faRightToBracket,
                    title: 'Login',
                    condition: () => !this.loggedIn,
                    onClick: () => {
                        this.$router.push({ name: 'login' });
                    },
                },
                {
                    name: 'register',
                    icon: faRocket,
                    header: 'Register',
                    condition: () => {
                        if (this.loggedIn) {
                            return false;
                        }
                        return SiteSettingsStore.getSetting()('REGISTER_ENABLED', Maybe.empty());
                    },
                    onClick: () => {
                        this.$router.push({ name: 'register' });
                    },
                },
                {
                    name: 'user',
                    icon: faCircleUser,
                    iconColor: () => {
                        if (!this.isStudentViewEnabled) {
                            return '';
                        }
                        // #856404 is the text color in warning alerts, but we
                        // don't have a variable for that.
                        return this.$inLTI ? '#856404' : 'var(--warning-theme-color)';
                    },
                    showUserNotifications: true,
                    title: () => {
                        const name = UserStore.loggedInUser().mapOrDefault(
                            user => user.getDisplayName(undefined).orDefault('You'),
                            utils.ELLIPSIS,
                        );
                        const studentViewAddition = this.isStudentViewEnabled
                            ? ' (student view)'
                            : '';
                        return `${name}${studentViewAddition}`;
                    },
                    header: 'User',
                    width: () => {
                        if (this.$root.$isLargeWindow) {
                            return 800;
                        } else {
                            return Math.min(800, this.$root.$windowWidth - 100);
                        }
                    },
                    component: 'user-info',
                    condition: () => this.loggedIn,
                    animateAdd: true,
                },
                {
                    name: 'courses',
                    icon: faBook,
                    header: 'Courses',
                    component: 'course-list',
                    condition: () => this.loggedIn && !this.simpleSidebar,
                    reload: true,
                    animateAdd: true,
                    showWhenLoading: true,
                },
                {
                    name: 'assignments',
                    icon: faPenToSquare,
                    header: 'Assignments',
                    component: 'assignment-list',
                    condition: () => this.loggedIn && !this.simpleSidebar,
                    reload: true,
                    animateAdd: true,
                    showWhenLoading: true,
                },
                {
                    name: 'ltiAssignments',
                    icon: faPenToSquare,
                    title: 'Assignments',
                    header: () =>
                        this.ltiAssignment.mapOrDefault(assig => assig.course.name, 'Assignments'),
                    component: 'assignment-list',
                    condition: () => this.canManageCurrentLtiAssignment,
                    reload: true,
                    data: () => this.ltiAssignment.extract(),
                    animateAdd: true,
                },
                {
                    name: 'studentViewAssignments',
                    icon: faPenToSquare,
                    title: 'Assignments',
                    header: () => this.course?.name ?? 'Assignments',
                    component: 'assignment-list',
                    condition: () => this.isStudentViewEnabled,
                    reload: true,
                    data: () => ({ course: this.course }),
                    animateAdd: true,
                },
                {
                    name: 'groups',
                    icon: 'fa-light fa-users',
                    header: 'Groups',
                    component: 'group-list',
                    data: () => ({ course: this.course }),
                    condition: () =>
                        this.loggedIn &&
                        this.course &&
                        SiteSettingsStore.getSetting()('GROUPS_ENABLED', this.course.tenantId) &&
                        this.course.groupSets.length > 0,
                    reload: true,
                    animateAdd: true,
                },
                {
                    name: 'cases',
                    icon: faMagnifyingGlass,
                    header: 'Plagiarism Cases',
                    component: 'plagiarism-case-list',
                    condition: () => this.loggedIn && this.$route.name === 'plagiarism_detail',
                    reload: true,
                    animate: true,
                },
                {
                    name: 'submissions',
                    icon: 'submissions',
                    header: 'Submissions',
                    component: 'submissions-sidebar-list',
                    condition: () =>
                        this.loggedIn &&
                        (this.$route.name === 'submission_file' ||
                            this.$route.name === 'submission'),
                    reload: true,
                    animate: true,
                },
            ],
            currentEntry: null,
            subMenus: [],
            mobileVisible: false,
            dimmingUseSpace: true,
        };
    },

    computed: {
        ...mapGetters('courses', ['getCourse']),
        ...mapGetters('assignments', ['getAssignment']),

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        darkMode() {
            return UIPrefsStore.darkMode();
        },

        /** @returns The currently logged in user. */
        loggedInUser() {
            return UserStore.loggedInUser().extract();
        },

        /** @returns Is there someone currently logged in. */
        loggedIn() {
            return this.loggedInUser != null;
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        ownTenantId() {
            return UserStore.tenantId();
        },

        /** @returns Whether we are currently in student view. */
        isStudentViewEnabled() {
            return UserStore.studentView().tag === 'enabled';
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        adminRoute() {
            if (UserStore.studentView().tag === 'enabled') {
                return Maybe.empty();
            }
            if (UserStore.canManageSite()) {
                return Maybe.of({ name: 'admin' });
            }

            return this.ownTenantId
                .filter(() => UserStore.canManageTenant()(this.ownTenantId))
                .map(tenantId => ({
                    name: 'tenant',
                    params: { tenantId },
                }));
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        ltiAssignment() {
            return this.getAssignment(this.$LTIAssignmentId);
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        canManageCurrentLtiAssignment() {
            if (this.loggedIn && this.$inLTI) {
                return this.ltiAssignment.mapOrDefault(
                    assig =>
                        assig.course.canManage || assig.course.assignments.some(a => a.canManage),
                    false,
                );
            }
            return false;
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        course() {
            return this.getCourse(this.courseId).extractNullable();
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        courseId() {
            return this.$routeParamAsId('courseId');
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        assignmentId() {
            return this.$routeParamAsId('assignmentId');
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        assignment() {
            return this.getAssignment(this.assignment).extract();
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        floating() {
            return (
                this.$inLTI ||
                !this.$root.$isMediumWindow ||
                this.mobileVisible ||
                this.$route.meta.floatingSidebar
            );
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        dimPage() {
            // Make sure all properties are accessed so vue's caching works correctly.
            const isFloating = this.floating;
            const isMenuOpen = this.subMenus.length > 0 || this.mobileVisible;
            const customWidth = this.currentEntry && this.maybeCall(this.currentEntry.width);

            return !!((isFloating && isMenuOpen) || customWidth);
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        hideInitialEntries() {
            return (
                this.$inLTI || !this.$root.$isMediumWindow || !this.$route.meta.initiallySidebarOpen
            );
        },

        /** @returns Whether we want to hide the normal sidebar, and show a minimal simple variant. */
        simpleSidebar() {
            return this.$inLTI || this.isStudentViewEnabled;
        },
    },

    watch: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        $route(newVal, oldVal) {
            if (newVal.name === oldVal.name) {
                return;
            }
            if (this.hideInitialEntries) {
                this.closeSubMenu(true);
            } else {
                this.setInitialEntry();
            }
        },

        assignmentId: {
            immediate: true,
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            handler(newVal) {
                if (newVal) {
                    utils.loadBackgroundPromises(
                        this.loadSingleAssignment({
                            assignmentId: newVal,
                            courseId: this.courseId,
                        }),
                    );
                }
            },
        },

        $LTIAssignmentId: {
            immediate: true,
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            handler(newVal) {
                if (newVal) {
                    utils.loadBackgroundPromises(
                        this.loadSingleAssignment({ assignmentId: newVal }),
                    );
                }
            },
        },
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    async mounted() {
        this.$root.$on('sidebar::show', submenu => {
            if (submenu === undefined) {
                this.toggleMobileSidebar();
            } else {
                this.openUpperSubMenu(this.findEntry(submenu), false);
            }
        });

        try {
            await this.loadCurrentCourse();
        } catch {
            // Load errors can be ignored.
        }
        this.$on('sidebar::close', this.onCloseSidebarEvent);
        this.$root.$on('cg::sidebar::close', this.onCloseSidebarEvent);

        this.setInitialEntry();
        this.loaded = true;
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    destroyed() {
        this.$root.$off('cg::sidebar::close', this.onCloseSidebarEvent);
    },

    methods: {
        ...mapActions('assignments', ['loadSingleAssignment']),
        ...mapActions('courses', ['loadSingleCourse']),
        ...mapActions('user', {
            logoutUser: 'logout',
        }),

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        onCloseSidebarEvent() {
            if (this.floating || this.mobileVisible) {
                this.closeSubMenu(true);
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async logout() {
            await this.logoutUser();
            this.closeSubMenu(true);
            this.$router.push({ name: 'login' });
            return {};
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        refreshItems() {
            this.loading = true;
            this.$root.$emit('sidebar::reload');
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        findEntry(name) {
            return this.entries.find(entry => entry.name === name);
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        setInitialEntry() {
            if (this.$route.name === 'login' || this.$route.name === 'register') {
                this.currentEntry = this.findEntry(this.$route.name);
            } else if (this.$route.name == null || !this.loggedIn || this.hideInitialEntries) {
                // NOOP
            } else if (this.$route.query.sbloc === 'm') {
                this.openMenuStack([this.findEntry('user')]);
            } else if (this.$route.query.sbloc === 'a') {
                this.openMenuStack([this.findEntry('assignments')]);
            } else if (this.$route.query.sbloc === 'c') {
                this.openMenuStack([this.findEntry('courses')]);
            } else if (this.$route.query.sbloc === 'g') {
                this.openMenuStack([this.findEntry('groups')]);
            } else {
                const menuStack = [this.findEntry('courses')];
                if (this.course != null) {
                    const course = this.course;
                    menuStack.push({
                        header: () =>
                            this.$createElement(CourseName, {
                                props: { course, hideDate: true },
                            }),
                        component: 'assignment-list',
                        data: { course },
                        reload: true,
                    });
                }
                this.openMenuStack(menuStack);
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        openMenuStack(stack) {
            if (stack.length === 0) {
                return;
            }

            this.openUpperSubMenu(stack[0]);

            const subMenus = stack.slice(1);
            for (let i = 0; i < subMenus.length; i++) {
                this.openSubMenu(subMenus[i]);
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        openUpperSubMenu(entry, toggle = false) {
            if (entry.onClick != null) {
                this.currentEntry = entry;
                entry.onClick();
            } else if (toggle && this.currentEntry && entry.name === this.currentEntry.name) {
                this.closeSubMenu(true);
            } else {
                const hadSubMenuOpen = this.subMenus.length > 0;

                this.currentEntry = entry;
                this.subMenus = [];
                this.openSubMenu(entry);

                this.dimmingUseSpace =
                    (this.dimPage && hadSubMenuOpen) || !this.maybeCall(entry.width);
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        openSubMenu(entry) {
            this.loading = false;
            this.subMenus.push(entry);
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        closeSubMenu(closeAll = false) {
            this.loading = false;

            this.$root.$emit('sidebar::submenu-closed');

            if (closeAll) {
                this.subMenus = [];
                this.currentEntry = null;
            } else {
                this.subMenus.pop();

                if (this.subMenus.length === 0) {
                    this.currentEntry = null;
                }
            }

            if (closeAll || this.subMenus.length === 0) {
                this.unsetSbloc();
                if (this.mobileVisible) {
                    this.toggleMobileSidebar();
                }
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        unsetSbloc() {
            const route = this.$route;
            const query = { ...route.query };
            if (query.sbloc != null) {
                delete query.sbloc;
            }
            this.$router.replace({ ...route, query });
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        toggleMobileSidebar() {
            this.mobileVisible = !this.mobileVisible;

            // Make document (un)scrollable
            if (this.mobileVisible) {
                document.body.style.height = '100%';
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.height = '';
                document.body.style.overflow = '';
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        maybeCall(fun) {
            switch (typeof fun) {
                case 'function':
                    return fun.bind(this)();
                default:
                    return fun;
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        subMenuStyle(subMenu) {
            const style = {};
            const width = this.maybeCall(subMenu.width);

            if (width) {
                style.width = '100vw';
                style.maxWidth = `${width}px`;
            }

            return style;
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        openRouteInTab() {
            return this.$http.post(
                '/api/v1/files/?max_age=30',
                JSON.stringify(
                    loggedInUserSessionParser.encode(UserStore.dangerousSession().extract()),
                ),
            );
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        afterOpenRouteInTab({ data: loginFile }) {
            const curRoute = JSON.stringify({
                name: this.$route.name,
                params: this.$route.params,
                query: this.$route.query,
                hash: this.$route.hash,
            });
            const newRoute = this.$router.resolve({
                name: 'login_and_redirect',
                params: {
                    loginFile: loginFile.name,
                },
                query: {
                    next: curRoute,
                },
            });

            window.open(newRoute.href, '_blank');
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async loadCurrentCourse() {
            if (this.courseId != null) {
                await this.loadSingleCourse({ courseId: this.courseId });
            }
        },
    },

    components: {
        Icon,
        Loader,
        UserInfo,
        CourseList,
        AssignmentList,
        PlagiarismCaseList,
        SubmissionsSidebarList,
        Announcements,
        SubmitButton,
        GroupList,
        CgLogo,
        NotificationBell,
        HelpHomepageLink,
        Vnode: {
            name: 'vnode',
            functional: true,
            render: (h, ctx) => {
                const { fun } = ctx.props;
                switch (typeof fun) {
                    case 'function': {
                        const result = fun();
                        if (typeof result === 'object') {
                            return result;
                        }
                        return h('span', null, [result]);
                    }
                    default:
                        return h('span', null, [fun]);
                }
            },
        },
        ToggleStudentViewButton,
    },
};
</script>

<style lang="less" scoped>
@import '~mixins.less';

.sidebar {
    position: fixed;
    position: sticky;
    // Must be greater than .sticky-top, .local-header, and modals
    z-index: 1035;
    top: 0;
    .vh(height, 100);

    &:not(.floating) {
        display: flex;
        flex-direction: row;
    }

    a {
        text-decoration: none;
        color: inherit;
    }
}

.svg-inline--fa.notification-bell {
    position: absolute;
    right: 0.75em;
    transition: opacity var(--transition-duration);
    &.shown {
        cursor: help;
    }
}

.main-menu {
    @main-menu-width: 6rem;

    display: flex;
    flex-direction: column;
    min-width: @main-menu-width;
    height: 100%;

    @media @media-no-small {
        width: @main-menu-width;
    }

    @media @media-small {
        position: absolute;
        right: 100%;
        transform: none;

        &.show {
            transform: translateX(100%);
        }
    }

    // Draw a box shadow around the main menu. This can't be done with the
    // box-shadow property on .main-class, because the z-indices of
    // .main-menu and .submenu must be equal, so that modals spawned in the
    // latter can have a higher z-index than .main-menu.
    & + .shadow-overlay {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        z-index: 1;
        pointer-events: none;

        @media @media-no-small {
            width: @main-menu-width;
        }

        @media @media-small {
            right: 0;
        }
    }

    .sidebar-top {
        flex: 1 1 auto;
        overflow-y: auto;
    }

    .sidebar-top-item {
        display: flex;
        flex-direction: column;
        padding: 0.75rem 0.5rem;

        @media @media-small {
            flex-direction: row;
            align-items: center;
            padding: 0.5rem;
        }

        &.logo {
            flex: 0 0 auto;
            display: block;
            padding: 1rem 0.5rem;

            img {
                width: 90%;
                margin: 0 auto;
                display: block;
            }
        }

        .svg-inline--fa {
            @media @media-no-small {
                display: block;
                margin: 0 auto;
                margin-bottom: 5px;
            }

            @media @media-small {
                margin-right: 0.5rem;
            }
        }

        .name {
            text-align: center;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }

    .sidebar-bottom {
        display: flex;
        flex-direction: row;
        flex: 0 0 auto;
    }

    .sidebar-bottom-item {
        flex: 1 1 auto;
        padding: 0.5rem 0.25rem 0.25rem;
        text-align: center;

        .svg-inline--fa {
            transform: translateY(-2px);
        }
    }
}

.submenu-container {
    width: 16rem;
    height: 100%;

    @media @media-small {
        z-index: 1;
    }

    .sidebar.floating & {
        position: absolute;
        top: 0;
        left: 100%;
    }

    .sidebar:not(.floating) &:not(.use-space) {
        width: 0;
    }

    header {
        display: flex;
        flex: 0 0 auto;

        .action {
            display: flex;
            align-items: center;
            flex: 0 0 auto;
            padding: 0.5rem 0.75rem;
            cursor: pointer;
        }

        .submenu-header {
            flex: 1 1 auto;
            margin: 0;
            padding: 0.5rem 0;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }

    .submenus {
        position: relative;
        height: 100%;
    }

    .submenu {
        display: flex;
        flex-direction: column;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;

        &:not(:last-child) {
            display: none;
        }

        // Draw a box shadow around the submenu. This can't be done with the
        // box-shadow property on .submenu, because the z-indices of
        // .main-menu and .submenu must be equal, so that modals spawned in the
        // latter can have a higher z-index than .main-menu.
        &::after {
            @shadow-size: 10px;

            content: '';
            display: block;
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: -1;
            pointer-events: none;
        }
    }
}

.page-overlay {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: -2;
}

.main-menu .new-tab-link.sidebar-bottom-item {
    padding: 0.5rem;

    &.submit-button:not(.state-default) .new-tab-wrapper {
        opacity: 0;
    }

    .svg-inline--fa {
        transform: none;
    }
}

.new-tab-wrapper {
    display: flex;
    justify-content: center;
}
</style>

<style lang="less">
.sidebar {
    & &-list-wrapper {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        max-height: 100%;
        margin: 0;
        padding: 0;
        overflow-y: hidden;
    }

    & &-footer {
        flex: 0 0 auto;
        display: flex;
        flex-direction: row;
    }

    & &-footer-button {
        flex: 1 1 auto;
        border: 0;
        border-radius: 0;
    }

    & &-list {
        list-style: none;
        margin: 0;
        padding: 0;
        flex: 1 1 auto;
        overflow-y: auto;

        .separator {
            margin: 0.5rem;
        }

        &.no-items-text {
            padding: 0 0.75rem;
        }
    }

    & &-list-section-header {
        padding: 0 0.75rem 0.25rem;
    }

    & &-top-item,
    & &-bottom-item {
        cursor: pointer;
    }

    & &-list-item {
        display: flex;
        flex-direction: row;
        cursor: pointer;
    }

    & &-item {
        padding: 0.5rem 0.75rem;
    }

    & &-filter {
        margin: 0.5rem;
    }

    hr.separator {
        margin: 0 0.5rem;
    }

    .submenu hr.separator {
        position: relative;
        z-index: 10;
    }

    small {
        display: block;
        line-height: 1.3;
    }
}
</style>
